import { useTranslations } from "next-intl";
import { type Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCountryConfig } from "@/config/country-context";
import { LoanCustomerInformationContext } from "@/features/customer-information/types";
import { findFinnishAccountIssue } from "@/features/customer-information/utils/finnish-account";
import { shouldShowLoanPurposeSelectFromContext } from "@/features/customer-information/utils/loan-purpose";
import {
	isValidNumberOfCars,
	shouldAskNumberOfCars,
} from "@/features/customer-information/utils/number-of-cars";
import {
	isManuallyEnteredSwedishAccount,
	isValidSwedishAccountNumber,
	isValidSwedishClearingNumber,
	isValidSwedishFullAccountNumber,
	swedishAccountDigits,
} from "@/features/customer-information/utils/swedish-account-fields";

export interface LoanCustomerForm {
	accountNumber: string;
	clearingNumber: string;
	/**
	 * SE: `accountNumber` already holds a whole clearing + account number, so it
	 * is validated on its total length and no clearing field is shown. Kept in
	 * the form values (not captured from a prop) so a prefill that arrives after
	 * the first render cannot be validated with the split-field rules.
	 */
	isWholeAccountNumber: boolean;
	/**
	 * The prefilled account number as it was resolved, so an untouched prefill
	 * can be told apart from one the customer typed over. Never submitted.
	 */
	prefilledAccountNumber: string;
	isPEP: boolean;
	citizenship: string[];
	/** Terms & conditions / autogiro consent, for main applicants where the country asks for it. */
	consent: boolean;
	/** Shown when the application purpose was "Other". */
	loanPurpose: string;
	/** Payment due day of month, where the country lets the customer choose. */
	dueDate: string;
	/** Asked when FLOW has no car count; `null` until answered. */
	numberOfCars: number | null;
}

interface UseLoanCustomerFormArgs {
	context: LoanCustomerInformationContext;
}

/**
 * Builds the loan customer-information form: citizenship where the country
 * asks for it, and an account number for main applicants.
 */
export const useLoanCustomerForm = ({ context }: UseLoanCustomerFormArgs) => {
	const t = useTranslations("pages.CustomerInformationPage");
	const tErrors = useTranslations("forms.errors");
	const { rules } = useCountryConfig();

	const { isCoapplicant, accountNumbers } = context;
	const requiresAutogiroConsent = !isCoapplicant && rules.autogiroConsent;
	const showLoanPurposeSelect = shouldShowLoanPurposeSelectFromContext(context);
	const askNumberOfCars = shouldAskNumberOfCars(context);
	const hasMultipleTrustedAccounts =
		new Set((accountNumbers ?? []).map(swedishAccountDigits)).size > 1;

	const schema = z
		.object({
			accountNumber: z.string(),
			clearingNumber: z.string(),
			isWholeAccountNumber: z.boolean(),
			prefilledAccountNumber: z.string(),
			isPEP: z.boolean({
				error: tErrors("general.required"),
			}),
			citizenship: z.array(z.string()),
			consent: z.boolean(),
			loanPurpose: z.string(),
			dueDate: z.string(),
			numberOfCars: z.number().nullable(),
		})
		.superRefine((data, ctx) => {
			if (rules.fixedCitizenship === null) {
				if (!data.citizenship || data.citizenship.length === 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: t("errors.mandatoryField"),
						path: ["citizenship"],
					});
				}
			}

			if (!isCoapplicant) {
				if (rules.payoutAccount === "swedish") {
					// The select and the prefilled input both hold whole numbers;
					// only manual entry is split into clearing and account fields.
					const validatesWholeAccountNumber =
						data.isWholeAccountNumber || hasMultipleTrustedAccounts;

					if (!validatesWholeAccountNumber) {
						if (!data.clearingNumber.trim()) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								message: t("errors.mandatoryField"),
								path: ["clearingNumber"],
							});
						} else if (!isValidSwedishClearingNumber(data.clearingNumber)) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								message: t("errors.invalidClearingNumber"),
								path: ["clearingNumber"],
							});
						}
					}

					const isManualEntry = isManuallyEnteredSwedishAccount({
						accountNumber: data.accountNumber,
						prefilledAccountNumber: data.prefilledAccountNumber,
						isWholeAccountNumber: data.isWholeAccountNumber,
						hasMultipleTrustedAccounts,
					});

					if (!data.accountNumber.trim()) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: t("errors.mandatoryField"),
							path: ["accountNumber"],
						});
					} else if (isManualEntry) {
						const hasValidAccountFormat = validatesWholeAccountNumber
							? isValidSwedishFullAccountNumber(data.accountNumber)
							: isValidSwedishAccountNumber(data.accountNumber);

						if (!hasValidAccountFormat) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								message: t("errors.invalidAccountNumber"),
								path: ["accountNumber"],
							});
						}
					}
				} else if (rules.payoutAccount === "finnish-iban") {
					const accountIssue = findFinnishAccountIssue(data);

					if (accountIssue) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message:
								accountIssue === "mandatory"
									? t("errors.mandatoryField")
									: t("errors.invalidAccountNumber"),
							path: ["accountNumber"],
						});
					}
				}
			}

			if (rules.dueDateChoice && !isCoapplicant && !data.dueDate) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("errors.mandatoryField"),
					path: ["dueDate"],
				});
			}

			if (showLoanPurposeSelect && !data.loanPurpose) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("errors.mandatoryField"),
					path: ["loanPurpose"],
				});
			}

			if (askNumberOfCars && !isValidNumberOfCars(data.numberOfCars)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						data.numberOfCars === null
							? t("errors.mandatoryField")
							: t("numberOfCars.invalid"),
					path: ["numberOfCars"],
				});
			}

			if (requiresAutogiroConsent && !data.consent) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("errors.mandatoryField"),
					path: ["consent"],
				});
			}
		});

	return {
		showLoanPurposeSelect,
		askNumberOfCars,
		// The third generic pins TTransformedValues to the same shape as
		// TFieldValues, matching the credit/my-money hooks (required since the
		// @hookform/resolvers v5 bump).
		useForm: useForm<LoanCustomerForm, unknown, LoanCustomerForm>({
			resolver: zodResolver(schema) as unknown as Resolver<
				LoanCustomerForm,
				unknown,
				LoanCustomerForm
			>,
			mode: "onTouched",
			defaultValues: {
				isPEP: undefined,
				citizenship: [],
				accountNumber: "",
				clearingNumber: "",
				isWholeAccountNumber: false,
				prefilledAccountNumber: "",
				consent: false,
				loanPurpose: "",
				dueDate: "",
				numberOfCars: null,
			},
		}),
		schema,
	};
};
